import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import { useToggle } from 'utils/hooks/useToggle';

import ExerciseHeader from '../ExerciseHeader';
import ExerciseIndicator from '../ExerciseIndicator';
import ExerciseControls from '../ExerciseControls';
import ExerciseInstructionsModal from '../ExerciseInstructionsModal';

const isSafariMobile = () => {
  const ua = navigator.userAgent;
  return /Safari/.test(ua) && /AppleWebKit/.test(ua) && !/Chrome|Chromium|CriOS|Edg|OPR/.test(ua);
};

const Exercises = ({
  isAssistantView,
  currentLap,
  selectedContent,
  setDisplayAlert,
  changeSessionState,
  onNextLap,
  onCompleteSession,
  changeViewStatus,
  selectContent,
}) => {
  const { laps = 1, exercises = [] } = selectedContent;

  const [currentExerciseNumber, setCurrentExerciseNumber] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [displayInstructions, setDisplayInstructions] = useToggle();

  const audioRef = useRef(null);
  const videoRef = useRef(null);
  const video = videoRef.current || {};
  const audio = audioRef.current || {};
  const currentExercise = isAssistantView
    ? selectedContent
    : exercises.find(({ position }) => position === currentExerciseNumber);
  const { exercise: { title = '', reps = '1', instructions = '' } = {} } = currentExercise;
  const steps = isAssistantView
    ? [{ stepNumber: 1 }]
    : [...Array(exercises.length)].map((_, index) => ({ stepNumber: index + 1 }));
  const isVideoEnd = currentTime > 0 && currentTime >= duration;
  const isLastExercise = isAssistantView || currentExerciseNumber === exercises.length;
  const isCircuitEnd = isAssistantView || (currentLap === laps && isLastExercise);

  useEffect(() => {
    const videoInstance = videoRef.current;

    const handleTimeUpdate = () => setCurrentTime(videoInstance.currentTime);
    const handleLoadedMetadata = () => setDuration(videoInstance.duration);

    videoInstance.addEventListener('timeupdate', handleTimeUpdate);
    videoInstance.addEventListener('loadedmetadata', handleLoadedMetadata);

    return () => {
      videoInstance.removeEventListener('timeupdate', handleTimeUpdate);
      videoInstance.removeEventListener('loadedmetadata', handleLoadedMetadata);
    };
  }, [videoRef.current]);

  useEffect(() => {
    if (isAssistantView && isVideoEnd) {
      return;
    }
    if (isCircuitEnd && isVideoEnd) {
      onCompleteSession();
    } else if (isLastExercise && isVideoEnd) {
      onNextLap();
      changeSessionState();
    } else if (isVideoEnd) {
      video.currentTime = 0;
      audio.currentTime = 0;
      setCurrentExerciseNumber((prevExercise) => prevExercise + 1);
    }
  }, [currentTime, duration]);

  const handleProgressChange = (e) => {
    const newTime = parseFloat(e.target.value);
    video.currentTime = newTime;
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const togglePlayPause = () => {
    if (video.paused) {
      video.play();
      audio.play();
    } else {
      video.pause();
      audio.pause();
    }
  };

  const handleMuteAudio = () => {
    if (audio.muted) {
      audio.muted = false;
    } else {
      audio.muted = true;
    }
  };

  const handlePreviousExercise = () => {
    video.currentTime = 0;
    audio.currentTime = 0;
    setCurrentExerciseNumber((prev) => prev - 1);
  };

  const handleNextExercise = () => {
    if (isAssistantView) {
      selectContent(null);
      changeViewStatus();
      return;
    }
    video.currentTime = 0;
    audio.currentTime = 0;
    if (isCircuitEnd) {
      changeViewStatus();
    }
    if (isLastExercise) {
      onNextLap();
      changeSessionState();
    } else {
      video.currentTime = 0;
      audio.currentTime = 0;
      setCurrentExerciseNumber((prevExercise) => prevExercise + 1);
    }
  };

  const exercisesToDisplay = isAssistantView ? [currentExercise] : exercises;

  return (
    <>
      <section className="exercises">
        <ExerciseHeader title={title} audio={audio} setDisplayAlert={setDisplayAlert} muteAudio={handleMuteAudio} />
        <section className="exercises__video">
          {exercisesToDisplay.map((currentEx) => {
            const {
              position = 1,
              exercise: { id = '', videoUrl = '' },
            } = currentEx;
            if (position === currentExerciseNumber) {
              return (
                <video
                  key={id}
                  preload="auto"
                  ref={videoRef}
                  muted
                  autoPlay={!isSafariMobile()}
                  playsInline
                  controls={false}
                  controlsList="nodownload nofullscreen noremoteplayback"
                  disablePictureInPicture
                  className="exercises__video_content"
                >
                  <source src={videoUrl} type="video/mp4" />
                  <track kind="captions" />
                  Your browser does not support the video tag.
                </video>
              );
            }
            return null;
          })}
          {exercisesToDisplay.map((currentEx) => {
            const {
              position = 1,
              exercise: { id = '', audio: audioSrc = '' },
            } = currentEx;
            if (position === currentExerciseNumber) {
              return (
                <audio
                  key={id}
                  preload="auto"
                  ref={audioRef}
                  controls={false}
                  autoPlay={!isSafariMobile()}
                  src={audioSrc}
                >
                  <track kind="captions" />
                  Your browser does not support the
                  <code>audio</code> element.
                </audio>
              );
            }
            return null;
          })}
          <ExerciseIndicator
            steps={steps}
            exerciseNumber={currentExerciseNumber}
            setDisplayInstructions={setDisplayInstructions}
          />
        </section>
        <ExerciseControls
          video={video}
          currentExercise={currentExerciseNumber}
          duration={duration}
          currentTime={currentTime}
          lap={currentLap}
          reps={reps}
          isCircuitEnd={isCircuitEnd}
          isContentRecommendation={isAssistantView}
          changeProgress={handleProgressChange}
          togglePlay={togglePlayPause}
          goBack={handlePreviousExercise}
          goNext={handleNextExercise}
        />
      </section>
      {displayInstructions && (
        <ExerciseInstructionsModal
          modalIsOpen={displayInstructions}
          toggleModal={setDisplayInstructions}
          title={title}
          instructions={instructions}
        />
      )}
    </>
  );
};

Exercises.defaultProps = {
  isAssistantView: false,
};

Exercises.propTypes = {
  isAssistantView: PropTypes.bool,
  currentLap: PropTypes.number.isRequired,
  selectedContent: PropTypes.shape({
    laps: PropTypes.number,
    exercises: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  setDisplayAlert: PropTypes.func.isRequired,
  changeSessionState: PropTypes.func.isRequired,
  onNextLap: PropTypes.func.isRequired,
  onCompleteSession: PropTypes.func.isRequired,
  changeViewStatus: PropTypes.func.isRequired,
  selectContent: PropTypes.func.isRequired,
};

export default Exercises;
